import { createSlice, createAsyncThunk, createAction } from "@reduxjs/toolkit";
import {
  doc,
  getDoc,
  collection,
  addDoc,
  query,
  where,
  getDocs,
  updateDoc,
  onSnapshot,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { auth, db, storage } from "../config/firebase";
// Update User type to include orgId
import type { User } from "../types";
import * as admin from "firebase-admin";

// Extend User type to ensure orgId is included
interface ExtendedUser extends User {
  orgId?: string;
  uid?: string;
  teamRole?: string;
  totalSessions?: number;
  totalEarnings?: number;
  customerTotalAmount?: number;
  expertTotalEarnings?: number;
  ratings?: number[];
  lastActivity?: string | null;
  lastActivityFull: string | null;
  createdAt?: any;
  [key: string]: any;
}

import {
  getDatabase,
  update,
  ref as databaseRef,
  get as databaseGet,
} from "firebase/database";
import {
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from "firebase/auth";
import * as Sentry from "@sentry/react";
import { captureFirebaseError } from "../config/sentry";
import {
  getCustomerProfile,
  getExpertProfile,
  subscribeToProfile,
} from "../services/profileService";

interface Activity {
  userId: string;
  userName: string;
  activityId: string;
  timestamp?: { seconds: number };
  [key: string]: any;
}

// Add new interface for chat sessions
interface ChatSession {
  id: string;
  userId: string;
  rating?: number;
  createdAt: any;
  status?: string;
  // Add other relevant fields
}

interface ProfileState {
  user: ExtendedUser | null;
  fetchedUsers: ExtendedUser[];
  fetchLoading: boolean;
  fetchError: string;
  activities: any[];
  notificationSettings: {
    email: boolean;
    SMS: boolean;
    phoneNumber: string;
  };
  availability: boolean;
  
  
  allActivityWithoutRole: Activity[];
  sessionActivities: Activity[];
  customerProfile: any | null;
  expertProfile: any | null;
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  // Separate data for customers and experts
  customers: ExtendedUser[];
  experts: ExtendedUser[];
  customersLoading: boolean;
  expertsLoading: boolean;
  customersError: string | null;
  expertsError: string | null;
  // Legacy field for backward compatibility
  allUsers: ExtendedUser[];
  allUsersLoading: boolean;
  allUsersError: string | null;
  organizations: any;
  organizationsLoading: boolean;
  // Cache control
  lastFetchTimestamp: { [key: string]: number };
  activeListeners: { [key: string]: () => void };
  cacheExpiry: number; // Cache duration in milliseconds
  // Session activities cache
  sessionActivitiesLastFetch: number;
  sessionActivitiesCache: Activity[];
  // Real-time sync status
  isRealTimeEnabled: boolean;
  connectionStatus: "connected" | "disconnected" | "connecting";
  syncInProgress: boolean;
  // User ratings and reviews state
  userRatings: {
    [userId: string]: {
      avgRating: number;
      totalReviews: number;
      reviews: string;
    };
  };
  ratingsLoading: { [userId: string]: boolean };
  quickAccessCounts: {
    helpCenter: number;
    supportChat: number;
    videoTutorials: number;
    tooltipPackage: number;
  };
  chatSessions: ChatSession[];
  loading: boolean;
  // Quick Answers (Chat Sessions) state
  allChatSessions: ChatSession[];
  chatSessionsLoading: boolean;
  chatSessionsError: string | null;
  chatSessionsLastFetch: number;
}

const initialState: ProfileState = {
  user: JSON.parse(localStorage.getItem("user")  || "null"),
  fetchedUsers: [],
  fetchLoading: false,
  fetchError: "",
  activities: [],
  notificationSettings: { email: false, SMS: false, phoneNumber: "" },
  availability: false,

  allActivityWithoutRole: [],
  sessionActivities: [],
  customerProfile: null,
  expertProfile: null,
  status: "idle",
  error: null,
  // Separate data for customers and experts
  customers: [],
  experts: [],
  customersLoading: false,
  expertsLoading: false,
  customersError: null,
  expertsError: null,
  // Legacy field for backward compatibility
  allUsers: [],
  allUsersLoading: false,
  allUsersError: null,
  organizations: [],
  organizationsLoading: false,
  lastFetchTimestamp: {},
  activeListeners: {},
  cacheExpiry: 5 * 1000, // Reduced to 5 seconds for faster real-time updates
  // Session activities cache
  sessionActivitiesLastFetch: 0,
  sessionActivitiesCache: [],
  // Real-time sync status
  isRealTimeEnabled: true,
  connectionStatus: "disconnected",
  syncInProgress: false,
  // User ratings and reviews state
  userRatings: {},
  ratingsLoading: {},
  quickAccessCounts: {
    helpCenter: 0,
    supportChat: 0,
    videoTutorials: 0,
    tooltipPackage: 0,
  },
  chatSessions: [],
  loading: false,
  // Quick Answers (Chat Sessions) state
  allChatSessions: [],
  chatSessionsLoading: false,
  chatSessionsError: null,
  chatSessionsLastFetch: 0,
};

const withSentryTransaction = async <T>(
  name: string,
  operation: string,
  func: () => Promise<T>
): Promise<T> => {
  const transaction = Sentry.startTransaction({
    name: `auth.${name}`,
    op: operation,
  });

  Sentry.configureScope((scope) => {
    scope.setSpan(transaction);
  });

  try {
    return await func();
  } catch (error) {
    captureFirebaseError(error as Error, {
      operation: name,
      transaction: transaction.toJSON(),
    });
    throw error;
  } finally {
    transaction.finish();
  }
};

// Async thunk to fetch customer profile with real-time updates
export const fetchCustomerProfile = createAsyncThunk(
  "profiles/fetchCustomerProfile",
  async (userId: string, { rejectWithValue, dispatch, getState }) => {
    try {
      const state = getState() as { profile: ProfileState };
      const cacheKey = `customerProfile_${userId}`;
      const now = Date.now();

      // Check if data exists in cache and is not expired
      const lastFetch = state.profile.lastFetchTimestamp[cacheKey] || 0;
      const isExpired = now - lastFetch > state.profile.cacheExpiry;

      // If data exists and cache is not expired, return cached data
      if (state.profile.customerProfile && !isExpired) {
        console.log("Using cached customer profile data");
        return state.profile.customerProfile;
      }

      // Clean up existing listener if any
      const existingListener = state.profile.activeListeners[cacheKey];
      if (existingListener) {
        existingListener();
      }

      return await withSentryTransaction(
        "fetchCustomerProfile",
        "data",
        async () => {
          // Set up real-time listener for profiles collection
          const profilesRef = collection(db, "profiles");
          const profileQuery = query(
            profilesRef,
            where("userId", "==", userId)
          );

          const unsubscribe = onSnapshot(profileQuery, (snapshot) => {
            if (!snapshot.metadata.fromCache) {
              console.log("Real-time update detected for customer profile");

              if (snapshot.empty) {
                dispatch({
                  type: "profile/updateCustomerProfileFromListener",
                  payload: null,
                  meta: { cacheKey, timestamp: Date.now() },
                });
                return;
              }

              const profileDoc = snapshot.docs[0];
              const profileData = {
                id: profileDoc.id,
                ...profileDoc.data(),
              };

              dispatch({
                type: "profile/updateCustomerProfileFromListener",
                payload: profileData,
                meta: { cacheKey, timestamp: Date.now() },
              });
            }
          });

          // Store the listener for cleanup
          dispatch({
            type: "profile/setActiveListener",
            payload: { key: cacheKey, listener: unsubscribe },
          });

          // Initial fetch
          const result = await getCustomerProfile(userId);

          if (result.success) {
            return result.profile;
          } else {
            return rejectWithValue(result.error);
          }
        }
      );
    } catch (error: any) {
      return rejectWithValue(
        error?.message || "Failed to fetch customer profile"
      );
    }
  }
);

// Async thunk to fetch expert profile with real-time updates
export const fetchExpertProfile = createAsyncThunk(
  "profiles/fetchExpertProfile",
  async (userId: string, { rejectWithValue, dispatch, getState }) => {
    try {
      const state = getState() as { profile: ProfileState };
      const cacheKey = `expertProfile_${userId}`;
      const now = Date.now();

      // Check if data exists in cache and is not expired
      const lastFetch = state.profile.lastFetchTimestamp[cacheKey] || 0;
      const isExpired = now - lastFetch > state.profile.cacheExpiry;

      // If data exists and cache is not expired, return cached data
      if (state.profile.expertProfile && !isExpired) {
        console.log("Using cached expert profile data");
        return state.profile.expertProfile;
      }

      // Clean up existing listener if any
      const existingListener = state.profile.activeListeners[cacheKey];
      if (existingListener) {
        existingListener();
      }

      return await withSentryTransaction(
        "fetchExpertProfile",
        "data",
        async () => {
          // Set up real-time listener for profiles collection
          const profilesRef = collection(db, "profiles");
          const profileQuery = query(
            profilesRef,
            where("userId", "==", userId)
          );

          const unsubscribe = onSnapshot(profileQuery, (snapshot) => {
            if (!snapshot.metadata.fromCache) {
              console.log("Real-time update detected for expert profile");

              if (snapshot.empty) {
                dispatch({
                  type: "profile/updateExpertProfileFromListener",
                  payload: null,
                  meta: { cacheKey, timestamp: Date.now() },
                });
                return;
              }

              const profileDoc = snapshot.docs[0];
              const profileData = {
                id: profileDoc.id,
                ...profileDoc.data(),
              };

              dispatch({
                type: "profile/updateExpertProfileFromListener",
                payload: profileData,
                meta: { cacheKey, timestamp: Date.now() },
              });
            }
          });

          // Store the listener for cleanup
          dispatch({
            type: "profile/setActiveListener",
            payload: { key: cacheKey, listener: unsubscribe },
          });

          // Initial fetch
          const result = await getExpertProfile(userId);

          if (result.success) {
            return result.profile;
          } else {
            return rejectWithValue(result.error);
          }
        }
      );
    } catch (error: any) {
      return rejectWithValue(
        error?.message || "Failed to fetch expert profile"
      );
    }
  }
);
// Async thunks
console.log();
export const fetchUserData = createAsyncThunk(
  "profile/fetchUserData",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const firebaseUser = auth.currentUser;
      console.log(firebaseUser, "firebaseUser");
      if (!firebaseUser) {
        console.error("No logged-in user found.");
        return rejectWithValue("");
      }

      // First fetch user data from users collection
      const userDocRef = doc(db, `users/${firebaseUser.uid}`);
      const userDoc = await getDoc(userDocRef);

      // Then check the profiles collection using a query
      const profilesRef = collection(db, "profiles");
      const q = query(profilesRef, where("userId", "==", firebaseUser.uid));
      const profileSnapshot = await getDocs(q);

      let profileData = {};

      // Get profile data if any profiles match the query
      if (!profileSnapshot.empty) {
        profileData = profileSnapshot.docs[0].data();
        console.log("Profile data found:", profileData);
      }
      if (userDoc.exists() || !profileSnapshot.empty) {
        const userData = userDoc.exists() ? userDoc.data() : {};

        // Merge user data with profile data, with profile data taking precedence
        const user: ExtendedUser = {
          id: firebaseUser.uid,
          // Always use profileData.name and profileData.profileImageUrl if present
          name: profileData.name || firebaseUser.email!,
          email: firebaseUser.email!,
          role: profileData.role,
          profileImageUrl: profileData.profileImageUrl,
          availability: profileData.availability,
          orgId: userData.orgId || profileData.orgId,
          uid: firebaseUser.uid,
          teamRole: userData.teamRole ,
          ...( profileData.specialities
            ? {
                specialities: profileData.specialities || userData.specialities,
              }
            : {}),
          ...(profileData.ratePerMinute || userData.ratePerMinute
            ? {
                ratePerMinute:
                  profileData.ratePerMinute || userData.ratePerMinute,
              }
            : {}),
          ...(profileData.bio || userData.bio
            ? {
                bio: profileData.bio || userData.bio,
              }
            : {}),
        };

        localStorage.setItem("user", JSON.stringify(user));
        localStorage.setItem("isAuthenticated", "true");

        // Fetch the appropriate profile based on user role
        if (user.role === "Expert" || user.role === "expert_owner") {
          dispatch(fetchExpertProfile(user.id));
        } else if (user.role === "Customer") {
          dispatch(fetchCustomerProfile(user.id));
        }

        return user;
      } else {
        console.error("User profile not found in Firestore.");
        return rejectWithValue("User profile not found in Firestore.");
      }
    } catch (error) {
      console.error("Failed to fetch user data:", error);
      return rejectWithValue("Failed to fetch user data");
    }
  }
);

export const fetchUserActivities = createAsyncThunk(
  "profile/fetchUserActivities",
  async (page: number = 1, { rejectWithValue }) => {
    try {
      const firebaseUser = auth.currentUser;

      if (!firebaseUser) {
        console.error("No logged-in user found.");
        return rejectWithValue("No logged-in user found.");
      }

      const activitiesCollection = collection(
        db,
        `users/${firebaseUser.uid}/activities`
      );
      const activitiesSnapshot = await getDocs(activitiesCollection);
      const activitiesList = activitiesSnapshot.docs
        .map((doc) => doc.data())
        .sort((a, b) => {
          const timestampA = a.timestamp?.seconds || 0;
          const timestampB = b.timestamp?.seconds || 0;
          return timestampB - timestampA;
        });

      return activitiesList;
    } catch (error) {
      console.error("Failed to fetch activities:", error);
      return rejectWithValue("Failed to fetch activities");
    }
  }
);

export const fetchUserActivitiesForAllUser = createAsyncThunk(
  "profile/fetchUserActivitiesForAllUser",
  async (_, { rejectWithValue }) => {
    try {
      return await withSentryTransaction(
        "fetchUserActivitiesForAlluser",
        "data",
        async () => {
          // Reference to the users collection
          const usersRef = collection(db, "users");

          // Fetch all users
          const userSnapshot = await getDocs(usersRef);

          const allActivityWithoutRole: Activity[] = [];

          // Loop through each user and fetch their activities
          for (const userDoc of userSnapshot.docs) {
            const userData = userDoc.data();
            const userId = userDoc.id;

            // Reference to the user's activities subcollection
            const activitiesCollection = collection(
              db,
              `users/${userId}/activities`
            );

            // Fetch activities for the current user
            const activitiesSnapshot = await getDocs(activitiesCollection);

            // Map activities and include user information
            const userActivities = activitiesSnapshot.docs
              .map((activityDoc) => ({
                activityId: activityDoc.id,
                ...activityDoc.data(),
                userId,
                userName: userData.name,
                userData,
              }))
              .filter(
                (activity) =>
                  activity.type === "Video Call" || activity.type === "Chat"
              );

            // Add user activities to the final array
            allActivityWithoutRole.push(...userActivities);
          }

          // Sort activities by timestamp
          allActivityWithoutRole.sort((a, b) => {
            const timestampA = a.timestamp?.seconds || 0;
            const timestampB = b.timestamp?.seconds || 0;
            return timestampB - timestampA;
          });

          return allActivityWithoutRole;
        }
      );
    } catch (error) {
      console.error("Failed to fetch activities for all users:", error);
      return rejectWithValue("Failed to fetch activities for all users");
    }
  }
);



export const uploadProfileImage = createAsyncThunk(
  "profile/uploadProfileImage",
  async (file: File, { getState, dispatch, rejectWithValue }) => {
    try {
      const firebaseUser = auth.currentUser;

      if (!firebaseUser) {
        throw new Error("User not logged in.");
      }

      const storageRef = ref(storage, `profileImages/${firebaseUser.uid}`);

      // Upload the file
      await uploadBytes(storageRef, file);

      // Get the download URL
      const imageUrl = await getDownloadURL(storageRef);

      // Update users collection with the new image URL
      await updateDoc(doc(db, `users/${firebaseUser.uid}`), {
        profileImageUrl: imageUrl,
      });

      // Update profiles collection with the new image URL
      try {
        const profilesRef = collection(db, "profiles");
        const q = query(profilesRef, where("userId", "==", firebaseUser.uid));
        const profileSnapshot = await getDocs(q);

        if (!profileSnapshot.empty) {
          const profileDoc = profileSnapshot.docs[0];
          await updateDoc(profileDoc.ref, {
            profileImageUrl: imageUrl,
          });
        }
      } catch (profileError) {
        console.warn(
          "Failed to update profile image in profiles collection:",
          profileError
        );
        // Continue execution even if profiles update fails
      }

      // Log activity
      dispatch(
        logActivity({
          userId: firebaseUser.uid,
          type: "Profile Image Update",
          metadata: { details: "Profile image updated" },
        })
      );

      // Refresh user data
      dispatch(fetchUserData());

      return imageUrl;
    } catch (error) {
      console.error("Failed to upload profile image:", error);
      return rejectWithValue("Failed to upload profile image");
    }
  }
);

export const logActivity = createAsyncThunk(
  "profile/logActivity",
  async (
    {
      userId,
      type,
      metadata = {},
    }: { userId: string; type: string; metadata?: object },
    { rejectWithValue }
  ) => {
    try {
      const activitiesRef = collection(db, `users/${userId}/activities`);
      const docRef = await addDoc(activitiesRef, {
        type,
        timestamp: new Date(),
        metadata,
      });
      return { type, timestamp: new Date(), metadata, id: docRef.id };
    } catch (error) {
      console.error("Failed to log activity:", error);
      return rejectWithValue("Failed to log activity");
    }
  }
);

export const fetchNotificationSettings = createAsyncThunk(
  "profile/fetchNotificationSettings",
  async (_, { rejectWithValue }) => {
    try {
      const user = auth.currentUser;
      if (!user) {
        console.error("No logged-in user found.");
        return rejectWithValue("No logged-in user found.");
      }

      // Reference to the user's notification settings in Realtime Database
      const dbRef = databaseRef(getDatabase(), `users/${user.uid}/setting`);
      const snapshot = await databaseGet(dbRef);

      if (snapshot.exists()) {
        const notificationSettings = snapshot.val();
        console.log(" snapshot.val()", snapshot.val());
        // Only return defaults if all keys are missing or falsy
        if (
          notificationSettings &&
          (notificationSettings.email !== undefined ||
            notificationSettings.SMS !== undefined ||
            notificationSettings.phoneNumber !== undefined)
        ) {
          return {
            email: Boolean(notificationSettings.email),
            SMS: Boolean(notificationSettings.SMS),
            phoneNumber: notificationSettings.phoneNumber || "",
          };
        } else {
          return { email: false, SMS: false, phoneNumber: "" };
        }
      } else {
        return { email: false, SMS: false, phoneNumber: "" };
      }
    } catch (error) {
      console.error("Failed to fetch notification settings:", error);
      return rejectWithValue("Failed to fetch notification settings");
    }
  }
);

export const toggleNotificationSetting = createAsyncThunk(
  "profile/toggleNotificationSetting",
  async (type: "Email" | "SMS", { getState, rejectWithValue }) => {
    try {
      console.log(
        "Toggling notification setting for type:",
        type,
        auth.currentUser
      );
      const user = auth.currentUser;
      if (!user) {
        console.error("No logged-in user found.");
        return rejectWithValue("No logged-in user found.");
      }

      const state = getState() as any;
      const currentSettings = state.profile.notificationSettings || {
        email: false,
        SMS: false,
        phoneNumber: "",
      };

      // Convert 'Email' type to 'email' for state property access
      const stateKey = type === "Email" ? "email" : type;

      const updatedSettings = {
        ...currentSettings,
        [stateKey]: !currentSettings[stateKey],
      };

      // Reference to the user's notification settings in Realtime Database
      const dbRef = databaseRef(getDatabase(), `users/${user.uid}/setting`);

      // Update the notification settings in the Realtime Database
      await update(dbRef, updatedSettings);

      return updatedSettings;
    } catch (error) {
      console.error(`Failed to toggle ${type} notification:`, error);
      return rejectWithValue(`Failed to toggle ${type} notification`);
    }
  }
);

export const toggleAvailability = createAsyncThunk(
  "profile/toggleAvailability",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as any;
      const user = state.profile.user;
      const currentAvailability = state.profile.availability;

      if (!user) {
        console.error("No logged-in user found.");
        return rejectWithValue("No logged-in user found.");
      }

      const updatedAvailability = !currentAvailability;

      const dbRef = databaseRef(getDatabase(), `users/${user.id}/setting`);
      await update(dbRef, { availability: updatedAvailability });

      return updatedAvailability;
    } catch (error) {
      console.error("Failed to toggle availability:", error);
      return rejectWithValue("Failed to toggle availability");
    }
  }
);

export const fetchAvailability = createAsyncThunk(
  "profile/fetchAvailability",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as any;
      const user = state.profile.user;

      if (!user) {
        console.error("No logged-in user found.");
        return rejectWithValue("No logged-in user found.");
      }

      const dbRef = databaseRef(
        getDatabase(),
        `users/${user.id}/setting/availability`
      );
      const snapshot = await databaseGet(dbRef);

      if (snapshot.exists()) {
        const availability = snapshot.val();
        return availability;
      } else {
        console.log("Availability data not found. Defaulting to false.");
        return false;
      }
    } catch (error) {
      console.error("Failed to fetch availability:", error);
      return rejectWithValue("Failed to fetch availability");
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  "profile/updateUserProfile",
  async (formData: Partial<User>, { dispatch, rejectWithValue }) => {
    try {
      if (!auth.currentUser) {
        return rejectWithValue("No user is currently logged in.");
      }

      const userDocRef = doc(db, `users/${auth.currentUser.uid}`);
      await updateDoc(userDocRef, formData);

      // Also update the profiles collection if a profile exists for this user
      try {
        const profilesRef = collection(db, "profiles");
        const q = query(
          profilesRef,
          where("userId", "==", auth.currentUser.uid)
        );
        const profileSnapshot = await getDocs(q);
        if (!profileSnapshot.empty) {
          const profileDoc = profileSnapshot.docs[0];
          // Only update fields if present in formData
          const profileUpdate: any = {};
          if (formData.specialities !== undefined)
            profileUpdate.specialities = formData.specialities;
          if (formData.name !== undefined) profileUpdate.name = formData.name;
          if (formData.ratePerMinute !== undefined)
            profileUpdate.ratePerMinute = formData.ratePerMinute;
          if (formData.bio !== undefined) profileUpdate.bio = formData.bio;
          if (formData.availability !== undefined)
            profileUpdate.availability = formData.availability;
          if (Object.keys(profileUpdate).length > 0) {
            await updateDoc(profileDoc.ref, profileUpdate);
          }
        }
      } catch (profileError) {
        console.warn(
          "Failed to update specialities/name/ratePerMinute/bio/availability in profiles collection:",
          profileError
        );
      }

    

      // Refresh user data
      return dispatch(fetchUserData()).unwrap();
    } catch (error) {
      console.error("Failed to update profile:", error);
      return rejectWithValue("Failed to update profile");
    }
  }
);

export const updateUserPassword = createAsyncThunk(
  "profile/updateUserPassword",
  async (
    {
      newPassword,
      currentPassword,
    }: { newPassword: string; currentPassword: string },
    { rejectWithValue }
  ) => {
    try {
      const user = auth.currentUser;
      if (!user) {
        return rejectWithValue("No user is currently logged in.");
      }

      if (!user.email) {
        return rejectWithValue("User email not found.");
      }

      try {
        // First, try to update the password directly
        await updatePassword(user, newPassword);
        console.log("Password updated successfully without re-authentication");
        return true;
      } catch (error: any) {
        // If it fails due to requires-recent-login, re-authenticate first
        if (error?.code === "auth/requires-recent-login") {
          console.log(
            "Re-authentication required, attempting to re-authenticate..."
          );

          if (!currentPassword) {
            return rejectWithValue(
              "Current password is required for this operation. Please provide your current password."
            );
          }

          try {
            // Create credential with current password
            const credential = EmailAuthProvider.credential(
              user.email,
              currentPassword
            );

            // Re-authenticate the user
            await reauthenticateWithCredential(user, credential);
            console.log("Re-authentication successful");

            // Now try to update the password again
            await updatePassword(user, newPassword);
            console.log(
              "Password updated successfully after re-authentication"
            );
            return true;
          } catch (reauthError: any) {
            console.error("Re-authentication failed:", reauthError);

            if (reauthError?.code === "auth/wrong-password") {
              return rejectWithValue(
                "Current password is incorrect. Please check and try again."
              );
            } else if (reauthError?.code === "auth/too-many-requests") {
              return rejectWithValue(
                "Too many failed attempts. Please try again later."
              );
            } else if (reauthError?.code === "auth/weak-password") {
              return rejectWithValue(
                "New password is too weak. Please choose a stronger password."
              );
            } else {
              return rejectWithValue(
                `Re-authentication failed: ${
                  reauthError.message || "Unknown error"
                }`
              );
            }
          }
        } else {
          // Handle other password update errors
          if (error?.code === "auth/weak-password") {
            return rejectWithValue(
              "New password is too weak. Please choose a stronger password (at least 6 characters)."
            );
          } else if (error?.code === "auth/too-many-requests") {
            return rejectWithValue(
              "Too many password change attempts. Please try again later."
            );
          } else {
            console.error("Password update failed:", error);
            return rejectWithValue(
              `Failed to update password: ${error.message || "Unknown error"}`
            );
          }
        }
      }
    } catch (error: any) {
      console.error("Failed to update password:", error);
      return rejectWithValue(
        `Failed to update password: ${error.message || "Unknown error"}`
      );
    }
  }
);

// Add a new async thunk for updating session activity
export const updateSessionActivity = createAsyncThunk(
  "profile/updateSessionActivity",
  async (
    { activityId, updateData }: { activityId: string; updateData: any },
    { rejectWithValue }
  ) => {
    try {
      // Import firebase services
      const { doc, updateDoc } = await import("firebase/firestore");
      const { db } = await import("../config/firebase");

      // Reference to the sessionActivity document
      const sessionActivityRef = doc(db, "sessionActivity", activityId);

      // Update the activity in sessionActivity collection
      await updateDoc(sessionActivityRef, {
        ...updateData,
        updatedAt: new Date(),
      });

      console.log("Activity updated in Firebase:", updateData);

      // Return the updated data to update the Redux state
      return { activityId, ...updateData };
    } catch (error) {
      console.error("Error updating activity:", error);
      return rejectWithValue(error.message);
    }
  }
);

// Add a new async thunk for adding notes to session activity
export const addNoteToSessionActivity = createAsyncThunk(
  "profile/addNoteToSessionActivity",
  async (
    {
      activityId,
      note,
      allNotes,
    }: {
      activityId: string;
      note: { id: number; text: string; timestamp: string };
      allNotes: Array<{ id: number; text: string; timestamp: string }>;
    },
    { rejectWithValue }
  ) => {
    try {
      // Import firebase services
      const { doc, updateDoc } = await import("firebase/firestore");
      const { db } = await import("../config/firebase");

      // Reference to the sessionActivity document
      const sessionActivityRef = doc(db, "sessionActivity", activityId);

      // Update the adminNotes field in the sessionActivity collection
      await updateDoc(sessionActivityRef, {
        adminNotes: allNotes,
        updatedAt: new Date(),
      });

      console.log("Admin note added successfully");

      // Return the updated notes to update the Redux state
      return { activityId, adminNotes: allNotes };
    } catch (error) {
      console.error("Error adding admin note:", error);
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error occurred"
      );
    }
  }
);

// Add a new async thunk for updating notes in session activity
export const updateActivityNotes = createAsyncThunk(
  "profile/updateActivityNotes",
  async (
    {
      activityId,
      notes,
    }: {
      activityId: string;
      notes: Array<{ id: number; text: string; timestamp: string }>;
    },
    { rejectWithValue }
  ) => {
    try {
      // Import firebase services
      const { doc, updateDoc } = await import("firebase/firestore");
      const { db } = await import("../config/firebase");

      // Reference to the sessionActivity document
      const sessionActivityRef = doc(db, "sessionActivity", activityId);

      // Update the adminNotes field in the sessionActivity collection
      await updateDoc(sessionActivityRef, {
        adminNotes: notes,
        updatedAt: new Date(),
      });

      console.log("Activity notes updated successfully");

      // Return the updated notes to update the Redux state
      return { activityId, adminNotes: notes };
    } catch (error) {
      console.error("Error updating activity notes:", error);
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error occurred"
      );
    }
  }
);

// Add action creators for updating activities in the store
export const updateActivityInStore = createAction<{
  activityId: string;
  expertRating?: number;
  expertReview?: string;
  customerRating?: number;
  customerReview?: string;
  updatedAt?: Date;
}>("profile/updateActivityInStore");

export const updateActivityNotesInStore = createAction<{
  activityId: string;
  adminNotes: Array<{
    id: number;
    text: string;
    timestamp: string;
  }>;
}>("profile/updateActivityNotesInStore");

const profileSlice = createSlice({
  name: "profile",
  initialState,
  reducers: {
    clearProfiles(state) {
      state.customerProfile = null;
      state.expertProfile = null;
      state.status = "idle";
      state.error = null;
    },
    // Add new reducer for immediate user data updates
    updateUserData: (state, action) => {
      const { userId, userData } = action.payload;

      // Update in customers array
      const customerIndex = state.customers.findIndex(
        (user) => user.id === userId
      );
      if (customerIndex !== -1) {
        state.customers[customerIndex] = {
          ...state.customers[customerIndex],
          ...userData,
          // Ensure userData is properly updated for disabled status
          userData: {
            ...state.customers[customerIndex].userData,
            ...userData,
            disabled:
              userData.disabled !== undefined
                ? userData.disabled
                : state.customers[customerIndex].userData?.disabled,
          },
        };
      }

      // Update in experts array
      const expertIndex = state.experts.findIndex((user) => user.id === userId);
      if (expertIndex !== -1) {
        state.experts[expertIndex] = {
          ...state.experts[expertIndex],
          ...userData,
          // Ensure userData is properly updated for disabled status
          userData: {
            ...state.experts[expertIndex].userData,
            ...userData,
            disabled:
              userData.disabled !== undefined
                ? userData.disabled
                : state.experts[expertIndex].userData?.disabled,
          },
        };
      }

      // Update in allUsers array for backward compatibility
      const allUserIndex = state.allUsers.findIndex(
        (user) => user.id === userId
      );
      if (allUserIndex !== -1) {
        state.allUsers[allUserIndex] = {
          ...state.allUsers[allUserIndex],
          ...userData,
          // Ensure userData is properly updated for disabled status
          userData: {
            ...state.allUsers[allUserIndex].userData,
            ...userData,
            disabled:
              userData.disabled !== undefined
                ? userData.disabled
                : state.allUsers[allUserIndex].userData?.disabled,
          },
        };
      }
    },
    // Add new reducer for immediate organization data updates
    updateOrganizationData: (state, action) => {
      const { orgId, orgData } = action.payload;
      if (state.organizations[orgId]) {
        state.organizations[orgId] = {
          ...state.organizations[orgId],
          ...orgData,
        };
      }
    },
    // Add new reducer for immediate profile updates
    updateProfileData: (state, action) => {
      const { userId, profileData } = action.payload;

      // Update user profile data in all relevant arrays
      const updateUserProfile = (user: ExtendedUser) => {
        if (user.id === userId) {
          return {
            ...user,
            ...profileData,
            // Ensure profile-specific fields are updated
            ratePerMinute: profileData.ratePerMinute || user.ratePerMinute,
            specialities: profileData.specialities || user.specialities,
            availability:
              profileData.availability !== undefined
                ? profileData.availability
                : user.availability,
            teamRole: profileData.teamRole || user.teamRole,
            name: profileData.name || user.name,
            email: profileData.email || user.email,
          };
        }
        return user;
      };

      state.customers = state.customers.map(updateUserProfile);
      state.experts = state.experts.map(updateUserProfile);
      state.allUsers = state.allUsers.map(updateUserProfile);
    },
    updateUsersFromListener: (state, action) => {
      state.allUsers = action.payload;
      state.allUsersLoading = false;
      state.allUsersError = null;

      // Update cache timestamp
      if (action.meta) {
        state.lastFetchTimestamp[action.meta.cacheKey] = action.meta.timestamp;
      }
    },
    // Add new reducers for updating customers and experts separately
    updateCustomersFromListener: (state, action) => {
      state.customers = action.payload;
      state.customersLoading = false;
      state.customersError = null;
      // Also update allUsers for backward compatibility
      state.allUsers = action.payload;
      state.allUsersLoading = false;
      state.allUsersError = null;

      // Update cache timestamp
      if (action.meta) {
        state.lastFetchTimestamp[action.meta.cacheKey] = action.meta.timestamp;
      }
    },
    updateExpertsFromListener: (state, action) => {
      state.experts = action.payload;
      state.expertsLoading = false;
      state.expertsError = null;
      // Also update allUsers for backward compatibility
      state.allUsers = action.payload;
      state.allUsersLoading = false;
      state.allUsersError = null;

      // Update cache timestamp
      if (action.meta) {
        state.lastFetchTimestamp[action.meta.cacheKey] = action.meta.timestamp;
      }
    },
    // Add new reducer for updating session activities from listener
    updateSessionActivitiesFromListener: (state, action) => {
      state.sessionActivities = action.payload;
      state.fetchLoading = false;
      state.fetchError = "";

      // Update cache timestamp
      if (action.meta) {
        state.sessionActivitiesLastFetch = action.meta.timestamp;
      }
    },
    // Add new reducer for updating organizations from listener
    updateOrganizationsFromListener: (state, action) => {
      state.organizations = { ...state.organizations, ...action.payload };
      state.organizationsLoading = false;

      // Update cache timestamp
      if (action.meta) {
        state.lastFetchTimestamp["organizations"] = action.meta.timestamp;
      }
    },
    // Add new reducer for updating profiles from listener
    updateProfilesFromListener: (state, action) => {
      const profiles = action.payload;

      // Update users with profile data
      const updateUserWithProfile = (user: ExtendedUser) => {
        const userProfile = profiles[user.id];
        if (userProfile) {
          return {
            ...user,
            ratePerMinute: userProfile.ratePerMinute || user.ratePerMinute,
            specialities: userProfile.specialities || user.specialities,
            bio: userProfile.bio || user.bio,
            availability:
              userProfile.availability !== undefined
                ? userProfile.availability
                : user.availability,
            teamRole: userProfile.teamRole || user.teamRole,
            userData: {
              ...user.userData,
              ratePerMinute:
                userProfile.ratePerMinute || user.userData?.ratePerMinute,
              specialities:
                userProfile.specialities || user.userData?.specialities,
              bio: userProfile.bio || user.userData?.bio,
              availability:
                userProfile.availability !== undefined
                  ? userProfile.availability
                  : user.userData?.availability,
              teamRole: userProfile.teamRole || user.userData?.teamRole,
            },
          };
        }
        return user;
      };

      state.customers = state.customers.map(updateUserWithProfile);
      state.experts = state.experts.map(updateUserWithProfile);
      state.allUsers = state.allUsers.map(updateUserWithProfile);

      // Update cache timestamp
      if (action.meta) {
        state.lastFetchTimestamp["profiles"] = action.meta.timestamp;
      }
    },
    // Add new reducer for updating wallets from listener
    updateWalletsFromListener: (state, action) => {
      const wallets = action.payload;

      // Update users with wallet data
      const updateUserWithWallet = (user: ExtendedUser) => {
        const userWallet = wallets[user.id];
        if (userWallet !== undefined) {
          return {
            ...user,
            walletBalance: userWallet,
          };
        }
        return user;
      };

      state.customers = state.customers.map(updateUserWithWallet);
      state.experts = state.experts.map(updateUserWithWallet);
      state.allUsers = state.allUsers.map(updateUserWithWallet);

      // Update cache timestamp
      if (action.meta) {
        state.lastFetchTimestamp["wallets"] = action.meta.timestamp;
      }
    },
    // Add new reducer for setting active listeners
    setActiveListener: (state, action) => {
      const { key, listener } = action.payload;
      state.activeListeners[key] = listener;
    },
    // Add new reducer for cleaning up listeners
    cleanupListener: (state, action) => {
      const { key } = action.payload;
      if (state.activeListeners[key]) {
        state.activeListeners[key]();
        delete state.activeListeners[key];
      }
    },
    // Add real-time sync status reducers
    setConnectionStatus: (state, action) => {
      state.connectionStatus = action.payload;
    },
    setSyncInProgress: (state, action) => {
      state.syncInProgress = action.payload;
    },
    setRealTimeEnabled: (state, action) => {
      state.isRealTimeEnabled = action.payload;
    },
    // Cache management reducers
    removeExpiredCache: (state, action) => {
      const { key } = action.payload;
      delete state.lastFetchTimestamp[key];
    },
    // Real-time profile update reducers
    updateCustomerProfileFromListener: (state, action) => {
      state.customerProfile = action.payload;
      state.status = "succeeded";
      state.error = null;

      // Update cache timestamp
      if (action.meta) {
        state.lastFetchTimestamp[action.meta.cacheKey] = action.meta.timestamp;
      }
    },
    updateExpertProfileFromListener: (state, action) => {
      state.expertProfile = action.payload;
      state.status = "succeeded";
      state.error = null;

      // Update cache timestamp
      if (action.meta) {
        state.lastFetchTimestamp[action.meta.cacheKey] = action.meta.timestamp;
      }
    },
    // Immediate data update reducers for instant UI feedback
    immediateUserUpdate: (state, action) => {
      const { userId, updates } = action.payload;

      const updateUser = (user: ExtendedUser) => {
        if (user.id === userId) {
          return { ...user, ...updates };
        }
        return user;
      };

      state.customers = state.customers.map(updateUser);
      state.experts = state.experts.map(updateUser);
      state.allUsers = state.allUsers.map(updateUser);
    },
    immediateOrganizationUpdate: (state, action) => {
      const { orgId, updates } = action.payload;
      if (state.organizations[orgId]) {
        state.organizations[orgId] = {
          ...state.organizations[orgId],
          ...updates,
        };
      }
    },
    immediateActivityUpdate: (state, action) => {
      const { activityId, updates } = action.payload;
      const activityIndex = state.sessionActivities.findIndex(
        (activity) =>
          activity.id === activityId || activity.activityId === activityId
      );
      if (activityIndex !== -1) {
        state.sessionActivities[activityIndex] = {
          ...state.sessionActivities[activityIndex],
          ...updates,
        };
      }
    },
    // Rating management reducers
    setRatingsLoading: (state, action) => {
      const { userId, loading } = action.payload;
      state.ratingsLoading[userId] = loading;
    },
    updateUserRatings: (state, action) => {
      const { userId, ratingsData } = action.payload;
      state.userRatings[userId] = ratingsData;
      state.ratingsLoading[userId] = false;
    },
    // Chat sessions reducers
    updateChatSessionsFromListener: (state, action) => {
      state.allChatSessions = action.payload;
      state.chatSessionsLoading = false;
      state.chatSessionsError = null;

      // Update cache timestamp
      if (action.meta) {
        state.chatSessionsLastFetch = action.meta.timestamp;
      }
    },
    updateQuickAccessCountsFromListener: (state, action) => {
      // Always update with the latest real-time data
      state.quickAccessCounts = {
        helpCenter: action.payload.helpCenter || 0,
        supportChat: action.payload.supportChat || 0,
        videoTutorials: action.payload.videoTutorials || 0,
        tooltipPackage: action.payload.tooltipPackage || 0,
      };
    },
    // Real-time availability update
    updateAvailabilityFromListener: (state, action) => {
      if (state.user && state.user.uid === action.payload.userId) {
        state.user.availability = action.payload.availability;
      }
      if (state.expertProfile && state.expertProfile.userId === action.payload.userId) {
        state.expertProfile.availability = action.payload.availability;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch customer profile
      .addCase(fetchCustomerProfile.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchCustomerProfile.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.customerProfile = action.payload;
      })
      .addCase(fetchCustomerProfile.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      })

      // Fetch expert profile
      .addCase(fetchExpertProfile.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchExpertProfile.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.expertProfile = action.payload;
      })
      .addCase(fetchExpertProfile.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      });
    builder
      .addCase(fetchUserData.pending, (state) => {
        state.fetchLoading = true;
      })
      .addCase(fetchUserData.fulfilled, (state, action) => {
        state.user = action.payload;
        state.fetchLoading = false;
        state.fetchError = "";
      })
      .addCase(fetchUserData.rejected, (state, action) => {
        state.fetchLoading = false;
        state.fetchError = action.payload as string;
      })

      // fetchUserActivities
      .addCase(fetchUserActivities.pending, (state) => {
        state.fetchLoading = true;
      })
      .addCase(fetchUserActivities.fulfilled, (state, action) => {
        state.activities = action.payload;
        state.fetchLoading = false;
        state.fetchError = "";
      })
      .addCase(fetchUserActivities.rejected, (state, action) => {
        state.fetchLoading = false;
        state.fetchError = action.payload as string;
      })

      // fetchUserActivitiesForAllUser
      .addCase(fetchUserActivitiesForAllUser.pending, (state) => {
        state.fetchLoading = true;
      })
      .addCase(fetchUserActivitiesForAllUser.fulfilled, (state, action) => {
        state.allActivityWithoutRole = action.payload;
        state.fetchLoading = false;
        state.fetchError = "";
      })
      .addCase(fetchUserActivitiesForAllUser.rejected, (state, action) => {
        state.fetchLoading = false;
        state.fetchError = action.payload as string;
      })

    

      // uploadProfileImage
      .addCase(uploadProfileImage.fulfilled, (state, action) => {
        if (state.user) {
          state.user.profileImageUrl = action.payload;
        }
      })

      // fetchNotificationSettings

      .addCase(fetchNotificationSettings.fulfilled, (state, action) => {
        console.log("payload",action.payload);
        state.notificationSettings = action.payload;
      })

      // toggleNotificationSetting
      .addCase(toggleNotificationSetting.fulfilled, (state, action) => {
        state.notificationSettings = action.payload;
      })

      // toggleAvailability
      .addCase(toggleAvailability.fulfilled, (state, action) => {
        state.availability = action.payload;
      }) // fetchAvailability
      .addCase(fetchAvailability.fulfilled, (state, action) => {
        state.availability = action.payload;
      })

      // updateUserProfile
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.user = action.payload;
      }) // fetchAllSessionActivities
      .addCase(fetchAllSessionActivities.pending, (state) => {
        // Only set loading if we don't have cached data
        if (state.sessionActivities.length === 0) {
          state.fetchLoading = true;
        }
        state.fetchError = "";
      })
      .addCase(fetchAllSessionActivities.fulfilled, (state, action) => {
        state.sessionActivities = action.payload;
        state.fetchLoading = false;
        state.fetchError = "";

        // Update cache timestamp
        state.sessionActivitiesLastFetch = Date.now();
      })
      .addCase(fetchAllSessionActivities.rejected, (state, action) => {
        state.fetchLoading = false;
        state.fetchError = action.payload as string;
      })
      // updateSessionActivity
      .addCase(updateSessionActivity.fulfilled, (state, action) => {
        const { activityId, ...updatedData } = action.payload;

        // Update the specific activity in the sessionActivities array
        const existingActivityIndex = state.sessionActivities.findIndex(
          (activity) => activity.activityId === activityId
        );
        if (existingActivityIndex !== -1) {
          // Activity found, update it
          state.sessionActivities[existingActivityIndex] = {
            ...state.sessionActivities[existingActivityIndex],
            ...updatedData,
          };
        }
      })
      // addNoteToSessionActivity
      .addCase(addNoteToSessionActivity.fulfilled, (state, action) => {
        const { activityId, adminNotes } = action.payload;

        // Update the specific activity's adminNotes in the sessionActivities array
        const existingActivityIndex = state.sessionActivities.findIndex(
          (activity) => activity.activityId === activityId
        );
        if (existingActivityIndex !== -1) {
          // Activity found, update its adminNotes
          state.sessionActivities[existingActivityIndex].adminNotes =
            adminNotes;
        }
      }) // updateActivityNotes
      .addCase(updateActivityNotes.fulfilled, (state, action) => {
        const { activityId, adminNotes } = action.payload;

        // Update the specific activity's adminNotes in the sessionActivities array
        const existingActivityIndex = state.sessionActivities.findIndex(
          (activity) => activity.activityId === activityId
        );
        if (existingActivityIndex !== -1) {
          // Activity found, update its adminNotes
          state.sessionActivities[existingActivityIndex].adminNotes =
            adminNotes;
        }
      })
      .addCase(updateActivityInStore, (state, action) => {
        if (state.sessionActivities && state.sessionActivities.length > 0) {
          const { activityId, ...updateData } = action.payload;

          // Find and update the activity in the session activities array
          state.sessionActivities = state.sessionActivities.map((activity) => {
            if (
              activity.id === activityId ||
              activity.activityId === activityId
            ) {
              return { ...activity, ...updateData };
            }
            return activity;
          });
        }
      })
      // Update session activity with admin notes
      .addCase(updateActivityNotesInStore, (state, action) => {
        if (state.sessionActivities && state.sessionActivities.length > 0) {
          const { activityId, adminNotes } = action.payload;

          // Find and update the activity in the session activities array
          state.sessionActivities = state.sessionActivities.map((activity) => {
            if (
              activity.id === activityId ||
              activity.activityId === activityId
            ) {
              return { ...activity, adminNotes };
            }
            return activity;
          });
        }
      }) // fetchAllUsersByRole
      .addCase(fetchAllUsersByRole.pending, (state, action) => {
        const role = action.meta.arg;
        if (role === "Customer") {
          state.customersLoading = true;
          state.customersError = null;
        } else if (role === "Expert") {
          state.expertsLoading = true;
          state.expertsError = null;
        }
        // For backward compatibility
        if (state.allUsers.length === 0) {
          state.allUsersLoading = true;
        }
        state.allUsersError = null;
      })
      .addCase(fetchAllUsersByRole.fulfilled, (state, action) => {
        const role = action.meta.arg;
        if (role === "Customer") {
          state.customers = action.payload;
          state.customersLoading = false;
          state.customersError = null;
        } else if (role === "Expert") {
          state.experts = action.payload;
          state.expertsLoading = false;
          state.expertsError = null;
        }

        // For backward compatibility, update allUsers with the current role data
        state.allUsers = action.payload;
        state.allUsersLoading = false;
        state.allUsersError = null;

        // Update cache timestamp
        const cacheKey = action.meta?.arg
          ? `users_${action.meta.arg}`
          : "users";
        state.lastFetchTimestamp[cacheKey] = Date.now();
      })
      .addCase(fetchAllUsersByRole.rejected, (state, action) => {
        const role = action.meta.arg;
        if (role === "Customer") {
          state.customersLoading = false;
          state.customersError = action.payload as string;
        } else if (role === "Expert") {
          state.expertsLoading = false;
          state.expertsError = action.payload as string;
        }
        // For backward compatibility
        state.allUsersLoading = false;
        state.allUsersError = action.payload as string;
      })
      // fetchOrganization
      .addCase(fetchOrganization.pending, (state) => {
        state.organizationsLoading = true;
      })
      .addCase(fetchOrganization.fulfilled, (state, action) => {
        state.organizationsLoading = false;
        if (action.payload) {
          state.organizations[action.payload.id] = {
            id: action.payload.id,
            name: action.payload.name || "",
            billingAddress: action.payload.billingAddress || "",
            url: action.payload.url || "",
            taxId: action.payload.taxId || "",
          };
        }
      })
      .addCase(fetchOrganization.rejected, (state) => {
        state.organizationsLoading = false;
      })
      // fetchOrganizations
      .addCase(fetchOrganizations.pending, (state) => {
        state.organizationsLoading = true;
      })
      .addCase(fetchOrganizations.fulfilled, (state, action) => {
        state.organizationsLoading = false;
        state.organizations = { ...state.organizations, ...action.payload };
      })
      .addCase(fetchOrganizations.rejected, (state) => {
        state.organizationsLoading = false;
      })
      // fetchUserRatingAndReviews
      .addCase(fetchUserRatingAndReviews.pending, (state, action) => {
        const userId = action.meta.arg.userId;
        state.ratingsLoading[userId] = true;
      })
      .addCase(fetchUserRatingAndReviews.fulfilled, (state, action) => {
        const { userId, avgRating, totalReviews, reviews } = action.payload;
        state.userRatings[userId] = { avgRating, totalReviews, reviews };
        state.ratingsLoading[userId] = false;
      })
      .addCase(fetchUserRatingAndReviews.rejected, (state, action) => {
        const userId = action.meta.arg.userId;
        state.ratingsLoading[userId] = false;
      })
      // fetchQuickAccessCounts
      .addCase(fetchQuickAccessCounts.fulfilled, (state, action) => {
        state.quickAccessCounts = action.payload;
      })
      // fetchAllUsersQuickAccessCounts
      .addCase(fetchAllUsersQuickAccessCounts.fulfilled, (state, action) => {
        state.quickAccessCounts = action.payload;
      })
      // fetchChatSessions
      .addCase(fetchChatSessions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChatSessions.fulfilled, (state, action) => {
        state.loading = false;
        state.chatSessions = action.payload;
      })
      .addCase(fetchChatSessions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch chat sessions";
      })
      // fetchAllChatSessions
      .addCase(fetchAllChatSessions.pending, (state) => {
        state.chatSessionsLoading = true;
        state.chatSessionsError = null;
      })
      .addCase(fetchAllChatSessions.fulfilled, (state, action) => {
        state.chatSessionsLoading = false;
        state.allChatSessions = action.payload;
        state.chatSessionsLastFetch = Date.now();
      })
      .addCase(fetchAllChatSessions.rejected, (state, action) => {
        state.chatSessionsLoading = false;
        state.chatSessionsError = action.payload as string;
      });
  },
});

export default profileSlice.reducer;
export const {
  clearProfiles,
  updateUserData,
  updateOrganizationData,
  updateProfileData,
  updateUsersFromListener,
  updateCustomersFromListener,
  updateExpertsFromListener,
  updateSessionActivitiesFromListener,
  updateOrganizationsFromListener,
  updateProfilesFromListener,
  updateWalletsFromListener,
  setActiveListener,
  cleanupListener,
  setConnectionStatus,
  setSyncInProgress,
  setRealTimeEnabled,
  removeExpiredCache,
  updateCustomerProfileFromListener,
  updateExpertProfileFromListener,
  immediateUserUpdate,
  immediateOrganizationUpdate,
  immediateActivityUpdate,
  setRatingsLoading,
  updateUserRatings,
  updateChatSessionsFromListener,
  
  updateQuickAccessCountsFromListener,
  updateAvailabilityFromListener,
} = profileSlice.actions;
// Real-time subscription handler
export const subscribeToProfileUpdates =
  (profileId: string, profileType: "Customer" | "Expert") =>
  (dispatch: any) => {
    const handleUpdate = (updatedProfile: any) => {
      if (profileType === "Customer") {
        dispatch(clearProfiles());
        // Fetch updated profile
        dispatch(fetchCustomerProfile(profileId));
      } else if (profileType === "Expert") {
        dispatch(clearProfiles());
        // Fetch updated profile
        dispatch(fetchExpertProfile(profileId));
      }
    };

    return subscribeToProfile(profileId, handleUpdate);
  };

// Optimized session activities fetch with caching and real-time updates
export const fetchAllSessionActivities = createAsyncThunk(
  "profile/fetchAllSessionActivities",
  async (_, { getState, rejectWithValue, dispatch }) => {
    try {
      const state = getState() as { profile: ProfileState };
      const cacheKey = "sessionActivities";
      const now = Date.now();

      // Check if data exists in cache and is not expired
      const lastFetch = state.profile.sessionActivitiesLastFetch || 0;
      const isExpired = now - lastFetch > state.profile.cacheExpiry;

      // If data exists and cache is not expired, return cached data
      if (state.profile.sessionActivities.length > 0 && !isExpired) {
        console.log("Using cached session activities data");
        return state.profile.sessionActivities;
      }

      // Clean up existing listener if any
      const existingListener = state.profile.activeListeners[cacheKey];
      if (existingListener) {
        existingListener();
      }

      return await withSentryTransaction(
        "fetchAllSessionActivities",
        "data",
        async () => {
          // Reference to the sessionActivity collection
          const sessionActivitiesRef = collection(db, "sessionActivity");

          // Set up real-time listener for future updates
          const unsubscribe = onSnapshot(
            sessionActivitiesRef,
            async (snapshot) => {
              if (!snapshot.metadata.fromCache) {
                console.log("Real-time update detected for session activities");

                if (snapshot.empty) {
                  dispatch({
                    type: "profile/updateSessionActivitiesFromListener",
                    payload: [],
                    meta: { timestamp: Date.now() },
                  });
                  return;
                }

                // Process activities with batch user data fetching
                const activities = await processSessionActivitiesData(
                  snapshot.docs
                );

                // Update store with real-time data
                dispatch({
                  type: "profile/updateSessionActivitiesFromListener",
                  payload: activities,
                  meta: { timestamp: Date.now() },
                });
              }
            }
          );

          // Store the listener for cleanup
          dispatch({
            type: "profile/setActiveListener",
            payload: { key: cacheKey, listener: unsubscribe },
          });

          // Initial fetch
          const activitiesSnapshot = await getDocs(sessionActivitiesRef);

          if (activitiesSnapshot.empty) {
            return [];
          }

          // Process activities with batch user data fetching
          const activities = await processSessionActivitiesData(
            activitiesSnapshot.docs
          );

          return activities;
        }
      );
    } catch (error) {
      console.error("Failed to fetch session activities:", error);
      return rejectWithValue("Failed to fetch session activities");
    }
  }
);

// Helper function to process session activities data with batch operations
async function processSessionActivitiesData(docs: any[]) {
  try {
    if (docs.length === 0) return [];

    // Extract all unique user IDs (both customer and expert IDs)
    const userIds = new Set<string>();
    const activities = docs.map((doc) => {
      const data = doc.data();
      const activity = {
        activityId: doc.id,
        ...data,
      };

      if (activity.customerId) userIds.add(activity.customerId);
      if (activity.expertId) userIds.add(activity.expertId);

      return activity;
    });

    // Batch fetch all user data in parallel
    const userData = await batchFetchUserData(Array.from(userIds));

    // Attach user data to activities
    const enrichedActivities: Activity[] = activities.map((activity) => {
      // Attach customer data if customerId exists
      if (activity.customerId && userData[activity.customerId]) {
        activity.customerData = {
          uid: activity.customerId,
          ...userData[activity.customerId],
        };
      }

      // Attach expert data if expertId exists
      if (activity.expertId && userData[activity.expertId]) {
        activity.expertData = {
          uid: activity.expertId,
          ...userData[activity.expertId],
        };
      }

      return activity;
    });

    // Sort activities by timestamp (newest first)
    enrichedActivities.sort((a, b) => {
      const timestampA = a.startTime?.seconds || a.timestamp?.seconds || 0;
      const timestampB = b.startTime?.seconds || b.timestamp?.seconds || 0;
      return timestampB - timestampA;
    });

    return enrichedActivities;
  } catch (error) {
    console.error("Error processing session activities data:", error);
    return [];
  }
}

// Helper function to batch fetch user data
async function batchFetchUserData(
  userIds: string[]
): Promise<Record<string, any>> {
  try {
    const userData: Record<string, any> = {};

    if (userIds.length === 0) return userData;

    // Firestore 'in' operator has a limit of 10 items per query
    const batches = [];
    for (let i = 0; i < userIds.length; i += 10) {
      const batch = userIds.slice(i, i + 10);
      const usersRef = collection(db, "users");
      const userQuery = query(usersRef, where("__name__", "in", batch));
      batches.push(getDocs(userQuery));
    }

    // Execute all queries in parallel
    const results = await Promise.all(batches);

    // Process results
    results.forEach((snapshot) => {
      snapshot.docs.forEach((doc) => {
        userData[doc.id] = doc.data();
      });
    });

    return userData;
  } catch (error) {
    console.error("Error batch fetching user data:", error);
    return {};
  }
}

// Add new async thunk to fetch all users by role with caching and real-time updates
// Enhanced fetchAllUsersByRole with real-time capabilities
export const fetchAllUsersByRole = createAsyncThunk(
  "profile/fetchAllUsersByRole",
  async (
    role: "Customer" | "Expert",
    { getState, rejectWithValue, dispatch }
  ) => {
    try {
      const state = getState() as { profile: ProfileState };
      const cacheKey = `users_${role}`;
      const now = Date.now();

      // Check if data exists in cache and is not expired
      const lastFetch = state.profile.lastFetchTimestamp[cacheKey] || 0;
      const isExpired = now - lastFetch > state.profile.cacheExpiry;

      // Get the appropriate data based on role
      const currentData =
        role === "Customer" ? state.profile.customers : state.profile.experts;

      // If data exists and cache is not expired, return cached data
      if (currentData.length > 0 && !isExpired) {
        console.log(`Using cached ${role} data`);
        return currentData;
      }

      // Clean up existing listener if any
      const existingListener = state.profile.activeListeners[cacheKey];
      if (existingListener) {
        existingListener();
      }

      return await withSentryTransaction(
        "fetchAllUsersByRole",
        "data",
        async () => {
          // Reference to the users collection
          const usersRef = collection(db, "users");
          const q = query(usersRef, where("role", "==", role));

          // Set up real-time listener for future updates
          const unsubscribe = onSnapshot(q, async (snapshot) => {
            if (!snapshot.metadata.fromCache) {
              console.log(`Real-time update detected for ${role} users`);

              const userIds = snapshot.docs.map((doc) => doc.id);

              if (userIds.length === 0) {
                // Update the appropriate state based on role
                if (role === "Customer") {
                  dispatch({
                    type: "profile/updateCustomersFromListener",
                    payload: [],
                    meta: { cacheKey, timestamp: Date.now() },
                  });
                } else {
                  dispatch({
                    type: "profile/updateExpertsFromListener",
                    payload: [],
                    meta: { cacheKey, timestamp: Date.now() },
                  });
                }
                return;
              }

              // Batch fetch profiles and wallets in parallel
              const [profilesData, walletsData, activitiesData] =
                await Promise.all([
                  fetchProfilesBatch(userIds),
                  fetchWalletsBatch(userIds),
                  fetchActivitiesBatch(userIds),
                ]);

              const users: ExtendedUser[] = [];

              // Process each user with pre-fetched data
              for (const userDoc of snapshot.docs) {
                const userData = userDoc.data();
                const userId = userDoc.id;

                const profileData = profilesData[userId] || {};
                const walletBalance = walletsData[userId] || 0;
                const userActivities = activitiesData[userId] || [];

                // Calculate aggregated data from activities
                const {
                  totalSessions,
                  totalEarnings,
                  ratings,
                  lastActivity,
                  lastActivityFull,
                  customerTotalAmount,
                  expertTotalEarnings,
                } = calculateUserStats(userActivities);

                // Always use profileData.name and profileData.profileImageUrl if present
                const user: ExtendedUser = {
                  id: userId,
                  uid: userId,
                  name: profileData.name || userData.email || "Unknown",
                  email: userData.email || "",
                  role: userData.role,
                  teamRole: userData.teamRole,
                  profileImageUrl: profileData.profileImageUrl,
                  orgId: userData.orgId || profileData.orgId,
                  ratePerMinute: profileData.ratePerMinute ,
                  specialities: profileData.specialities ||  [],
                  bio: profileData.bio || userData.bio,
                  createdAt: userData.createdAt,
                  totalSessions,
                  totalEarnings,
                  customerTotalAmount,
                  expertTotalEarnings,
                  walletBalance,
                  ratings,
                  lastActivityFull,
                  lastActivity,
                  // Add userData for backward compatibility
                  userData: {
                    ...userData,
                    ...profileData,
                    teamRole: userData.teamRole || profileData.teamRole,
                    specialities:
                      profileData.specialities || userData.specialities || [],
                    availability: profileData.availability,
                    ratePerMinute:
                      profileData.ratePerMinute ,
                    disabled:
                      userData.disabled !== undefined
                        ? userData.disabled
                        : false,
                  },
                };

                users.push(user);
              }

              // Sort users by last activity (most recent first)
              users.sort((a, b) => {
                if (!a.lastActivity && !b.lastActivity) return 0;
                if (!a.lastActivity) return 1;
                if (!b.lastActivity) return -1;
                return (

                  new Date(b.lastActivity).getTime() -
                  new Date(a.lastActivity).getTime()
                );
              });

              // Update store with real-time data based on role
              if (role === "Customer") {
                dispatch({
                  type: "profile/updateCustomersFromListener",
                  payload: users,
                  meta: { cacheKey, timestamp: Date.now() },
                });
              } else {
                dispatch({
                  type: "profile/updateExpertsFromListener",
                  payload: users,
                  meta: { cacheKey, timestamp: Date.now() },
                });
              }
            }
          });

          // Store the listener for cleanup
          dispatch({
            type: "profile/setActiveListener",
            payload: { key: cacheKey, listener: unsubscribe },
          });

          // Set up real-time listeners for wallets and profiles
          const initialUserSnapshot = await getDocs(q);
          const initialUserIds = initialUserSnapshot.docs.map((doc) => doc.id);
          setupWalletListener(initialUserIds, dispatch, cacheKey);
          setupProfilesListener(initialUserIds, dispatch, cacheKey);

          // Initial fetch
          const userSnapshot = await getDocs(q);

          if (userSnapshot.empty) {
            return [];
          }

          const userIds = userSnapshot.docs.map((doc) => doc.id);

          // Batch fetch profiles and wallets in parallel
          const [profilesData, walletsData, activitiesData] = await Promise.all(
            [
              fetchProfilesBatch(userIds),
              fetchWalletsBatch(userIds),
              fetchActivitiesBatch(userIds),
            ]
          );

          const users: ExtendedUser[] = [];

          // Process each user with pre-fetched data
          for (const userDoc of userSnapshot.docs) {
            const userData = userDoc.data();
            const userId = userDoc.id;

            const profileData = profilesData[userId] || {};
            const walletBalance = walletsData[userId] || 0;
            const userActivities = activitiesData[userId] || [];

            // Calculate aggregated data from activities
            const {
              totalSessions,
              totalEarnings,
              ratings,
              lastActivity,
              lastActivityFull,
              customerTotalAmount,
              expertTotalEarnings,
            } = calculateUserStats(userActivities);

            // Always use profileData.name and profileData.profileImageUrl if present
            const user: ExtendedUser = {
              id: userId,
              uid: userId,
              name: profileData.name || userData.email || "Unknown",
              email: userData.email || "",
              role: userData.role,
              teamRole: userData.teamRole,
              profileImageUrl: profileData.profileImageUrl,
              orgId: userData.orgId || profileData.orgId,
              ratePerMinute: profileData.ratePerMinute ,
              specialities: profileData.specialities  || [],
              bio: profileData.bio || userData.bio,
              createdAt: userData.createdAt,
              totalSessions,
              totalEarnings,
              customerTotalAmount,
              expertTotalEarnings,
              walletBalance,
              ratings,
              lastActivity,
              lastActivityFull,
              // Add userData for backward compatibility
              userData: {
                ...userData,
                ...profileData,
                teamRole: userData.teamRole || profileData.teamRole,
                specialities:
                  profileData.specialities || userData.specialities || [],
                availability: profileData.availability,
                ratePerMinute: profileData.ratePerMinute ,
                disabled:
                  userData.disabled !== undefined ? userData.disabled : false,
              },
            };

            users.push(user);
          }

          // Sort users by last activity (most recent first)
          users.sort((a, b) => {
            if (!a.lastActivity && !b.lastActivity) return 0;
            if (!a.lastActivity) return 1;
            if (!b.lastActivity) return -1;
            return (
              new Date(b.lastActivity).getTime() -
              new Date(a.lastActivity).getTime()
            );
          });

          return users;
        }
      );
    } catch (error) {
      console.error("Failed to fetch users by role:", error);
      return rejectWithValue("Failed to fetch users by role");
    }
  }
);

// Helper function to batch fetch profiles
async function fetchProfilesBatch(
  userIds: string[]
): Promise<Record<string, any>> {
  try {
    const profilesRef = collection(db, "profiles");
    const profilesData: Record<string, any> = {};

    // If more than 10 users, batch the requests
    const batches = [];
    for (let i = 0; i < userIds.length; i += 10) {
      const batch = userIds.slice(i, i + 10);
      const batchQuery = query(profilesRef, where("userId", "in", batch));
      batches.push(getDocs(batchQuery));
    }

    const results = await Promise.all(batches);

    results.forEach((snapshot) => {
      snapshot.docs.forEach((doc) => {
        const data = doc.data();
        if (data.userId) {
          profilesData[data.userId] = data;
        }
      });
    });

    return profilesData;
  } catch (error) {
    console.error("Error batch fetching profiles:", error);
    return {};
  }
}

// Helper function to batch fetch wallets
async function fetchWalletsBatch(
  userIds: string[]
): Promise<Record<string, number>> {
  try {
    const walletPromises = userIds.map(async (userId) => {
      try {
        const walletRef = doc(db, "wallets", userId);
        const walletDoc = await getDoc(walletRef);
        return {
          userId,
          balance: walletDoc.exists() ? walletDoc.data()?.balance || 0 : 0,
        };
      } catch (error) {
        console.error(`Error fetching wallet for ${userId}:`, error);
        return { userId, balance: 0 };
      }
    });

    const walletResults = await Promise.all(walletPromises);
    const walletsData: Record<string, number> = {};

    walletResults.forEach(({ userId, balance }) => {
      walletsData[userId] = balance;
    });

    return walletsData;
  } catch (error) {
    console.error("Error batch fetching wallets:", error);
    return {};
  }
}

// Real-time wallet listener setup
const setupWalletListener = (
  userIds: string[],
  dispatch: any,
  cacheKey: string
) => {
  if (userIds.length === 0) return;

  // Set up real-time listener for wallets collection
  const walletsRef = collection(db, "wallets");

  const unsubscribeWallet = onSnapshot(walletsRef, (snapshot) => {
    if (!snapshot.metadata.fromCache) {
      console.log("Real-time update detected for wallets");

      const walletUpdates: Record<string, number> = {};
      snapshot.docs.forEach((doc) => {
        const userId = doc.id;
        if (userIds.includes(userId)) {
          walletUpdates[userId] = doc.data()?.balance || 0;
        }
      });

      // Dispatch wallet updates to store
      dispatch({
        type: "profile/updateWalletsFromListener",
        payload: walletUpdates,
        meta: { cacheKey: `${cacheKey}_wallets`, timestamp: Date.now() },
      });
    }
  });

  // Store the listener for cleanup
  dispatch({
    type: "profile/setActiveListener",
    payload: { key: `${cacheKey}_wallets`, listener: unsubscribeWallet },
  });
};

// Real-time profiles listener setup
const setupProfilesListener = (
  userIds: string[],
  dispatch: any,
  cacheKey: string
) => {
  if (userIds.length === 0) return;

  // Set up real-time listener for profiles collection
  const profilesRef = collection(db, "profiles");

  const unsubscribeProfiles = onSnapshot(profilesRef, (snapshot) => {
    if (!snapshot.metadata.fromCache) {
      console.log("Real-time update detected for profiles");

      const profileUpdates: Record<string, any> = {};
      snapshot.docs.forEach((doc) => {
        const profileData = doc.data();
        const userId = profileData.userId;
        if (userId && userIds.includes(userId)) {
          profileUpdates[userId] = {
            id: doc.id,
            ratePerMinute: profileData.ratePerMinute || 0,
            specialities: profileData.specialities || [],
            bio: profileData.bio || "",
            availability: profileData.availability || false,
            teamRole: profileData.teamRole || "",
            ...profileData,
          };
        }
      });

      // Dispatch profile updates to store
      dispatch({
        type: "profile/updateProfilesFromListener",
        payload: profileUpdates,
        meta: { cacheKey: `${cacheKey}_profiles`, timestamp: Date.now() },
      });
    }
  });

  // Store the listener for cleanup
  dispatch({
    type: "profile/setActiveListener",
    payload: { key: `${cacheKey}_profiles`, listener: unsubscribeProfiles },
  });
};

// Helper function to batch fetch activities from sessionActivity collection
async function fetchActivitiesBatch(
  userIds: string[]
): Promise<Record<string, any[]>> {
  try {
    const sessionActivitiesRef = collection(db, "sessionActivity");
    const activitiesData: Record<string, any[]> = {};

    // Initialize empty arrays for all users
    userIds.forEach((userId) => {
      activitiesData[userId] = [];
    });

    // Create batched queries for expertId and customerId
    // Firestore 'in' operator has a limit of 10 items per query
    const batches = [];

    for (let i = 0; i < userIds.length; i += 10) {
      const batch = userIds.slice(i, i + 10);

      // Query for activities where user is an expert
      const expertQuery = query(
        sessionActivitiesRef,
        where("expertId", "in", batch)
      );

      // Query for activities where user is a customer
      const customerQuery = query(
        sessionActivitiesRef,
        where("customerId", "in", batch)
      );

      batches.push(getDocs(expertQuery), getDocs(customerQuery));
    }

    // Execute all queries in parallel
    const results = await Promise.all(batches);

    // Process results and group by userId
    results.forEach((snapshot) => {
      snapshot.docs.forEach((doc) => {
        const activity = { id: doc.id, ...doc.data() } as any;

        // Add activity to expert's record if expertId exists
        if (activity.expertId && userIds.includes(activity.expertId)) {
          if (!activitiesData[activity.expertId]) {
            activitiesData[activity.expertId] = [];
          }

          // Transform to match expected format for expert activities
          const expertActivity = {
            type: activity.type || "Session",
            // Prioritize startTime for timestamp calculation
            timestamp: activity.startTime || activity.createdAt,
            metadata: {
              amount: activity.totalAmount ? `$${activity.totalAmount}` : "$0",
              cost: activity.cost || 0, // Add cost field for expert earnings
              rating: activity.expertRating?.toString() || "0",
              sessionId: activity.id,
              customerId: activity.customerId,
              duration: activity.duration || 0,
              // Store the raw timestamp for date formatting
              rawStartTime: activity.startTime,
            },
          };

          activitiesData[activity.expertId].push(expertActivity);
        }

        // Add activity to customer's record if customerId exists
        if (activity.customerId && userIds.includes(activity.customerId)) {
          if (!activitiesData[activity.customerId]) {
            activitiesData[activity.customerId] = [];
          }

          // Transform to match expected format for customer activities
          const customerActivity = {
            type: activity.type || "Session",
            // Prioritize startTime for timestamp calculation
            timestamp: activity.startTime || activity.createdAt,
            metadata: {
              amount: activity.totalAmount ? `$${activity.totalAmount}` : "$0",
              cost: activity.cost || 0, // Add cost field for customer total amount
              rating: activity.customerRating?.toString() || "0",
              sessionId: activity.id,
              expertId: activity.expertId,
              duration: activity.duration || 0,
              // Store the raw timestamp for date formatting
              rawStartTime: activity.startTime,
            },
          };

          activitiesData[activity.customerId].push(customerActivity);
        }
      });
    });

    return activitiesData;
  } catch (error) {
    console.error(
      "Error batch fetching activities from sessionActivity:",
      error
    );
    return {};
  }
}

// Common helper to calculate avg review and rating for a userId and role
export function calculateAvgReviewAndRating(
  activities: any[],
  userId: string,
  role: string
) {
  let ratings: number[] = [];
  let reviews: string[] = [];

  activities.forEach((activity) => {
    if (role === "Expert" && activity.expertId === userId) {
      // For experts, we look at customer ratings given to them
      if (activity.customerRating !== undefined) {
        const rating = parseFloat(activity.customerRating);
        if (!isNaN(rating) && rating > 0) ratings.push(rating);
      }
      if (
        activity.customerReview &&
        typeof activity.customerReview === "string" &&
        activity.customerReview.trim() !== ""
      ) {
        reviews.push(activity.customerReview);
      }
    } else if (role === "Customer" && activity.customerId === userId) {
      // For customers, we look at expert ratings given to them
      if (activity.expertRating !== undefined) {
        const rating = parseFloat(activity.expertRating);
        if (!isNaN(rating) && rating > 0) ratings.push(rating);
      }
      if (
        activity.expertReview &&
        typeof activity.expertReview === "string" &&
        activity.expertReview.trim() !== ""
      ) {
        reviews.push(activity.expertReview);
      }
    }
  });

  const avgRating =
    ratings.length > 0
      ? Number((ratings.reduce((a, b) => a + b, 0) / ratings.length).toFixed(1))
      : 0;
  const totalReviews = reviews.length;
  const avgReview = reviews.length > 0 ? reviews.join(" | ") : "";

  return { avgRating, avgReview, totalReviews, ratings };
}

// Helper function to calculate user statistics from activities
function calculateUserStats(activities: any[]) {
  let totalSessions = 0;
  let totalEarnings = 0;
  let customerTotalAmount = 0;
  let expertTotalEarnings = 0;
  const ratings: number[] = [];
  let lastActivityTimestamp = 0;
  let lastActivityRaw: any = null;

  activities.forEach((activity) => {
    // Count all session activities (from sessionActivity collection)
    if (
      activity.type === "Session" ||
      activity.type === "Video Call" ||
      activity.type === "Chat"
    ) {
      totalSessions++;

      // Calculate total amount from cost field
      if (
        activity.metadata?.cost &&
        typeof activity.metadata.cost === "number"
      ) {
        customerTotalAmount += activity.metadata.cost;
        expertTotalEarnings += activity.metadata.cost;
        totalEarnings += activity.metadata.cost;
      }

      // Fallback to amount field if cost is not available
      if (!activity.metadata?.cost && activity.metadata?.amount) {
        const amount = parseFloat(activity.metadata.amount.replace("$", ""));
        if (!isNaN(amount)) {
          totalEarnings += amount;
          customerTotalAmount += amount;
          expertTotalEarnings += amount;
        }
      }

      // Collect ratings
      if (activity.metadata?.rating) {
        const rating = parseFloat(activity.metadata.rating);
        if (!isNaN(rating) && rating > 0) {
          ratings.push(rating);
        }
      }

      // Prioritize startTime over timestamp for last activity tracking
      let activityTimestamp = 0;
      let rawTimestampData = null;

      // Check for startTime in metadata first (where sessionActivity data might be stored)
      if (activity.metadata?.rawStartTime) {
        rawTimestampData = activity.metadata.rawStartTime;
        if (rawTimestampData?.seconds) {
          activityTimestamp = rawTimestampData.seconds;
        } else if (rawTimestampData instanceof Date) {
          activityTimestamp = Math.floor(rawTimestampData.getTime() / 1000);
        } else if (typeof rawTimestampData === "number") {
          activityTimestamp = rawTimestampData;
        }
      }
      // Then check the activity's own startTime
      else if (activity.startTime) {
        rawTimestampData = activity.startTime;
        if (activity.startTime?.seconds) {
          activityTimestamp = activity.startTime.seconds;
        } else if (activity.startTime instanceof Date) {
          activityTimestamp = Math.floor(activity.startTime.getTime() / 1000);
        } else if (typeof activity.startTime === "number") {
          activityTimestamp = activity.startTime;
        }
      }
      // Finally fall back to timestamp if neither startTime is available
      else if (activity.timestamp) {
        rawTimestampData = activity.timestamp;
        if (activity.timestamp?.seconds) {
          activityTimestamp = activity.timestamp.seconds;
        } else if (activity.timestamp instanceof Date) {
          activityTimestamp = Math.floor(activity.timestamp.getTime() / 1000);
        } else if (typeof activity.timestamp === "number") {
          activityTimestamp = activity.timestamp;
        }
      }

      if (activityTimestamp > lastActivityTimestamp) {
        lastActivityTimestamp = activityTimestamp;
        lastActivityRaw = activity.metadata?.rawStartTime || activity.timestamp;
      }
    }
  });

  // Format date as MM/DD/YYYY for table view
  const lastActivity = lastActivityTimestamp
    ? new Date(lastActivityTimestamp * 1000).toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      })
    : "No activity";

  // Format date and time as MM/DD/YYYY, hh:mm:ss AM/PM for detailed view
  const lastActivityFull = lastActivityRaw
    ? formatFullTimestamp(lastActivityRaw)
    : "No activity";

  return {
    totalSessions,
    totalEarnings,
    customerTotalAmount,
    expertTotalEarnings,
    ratings,
    lastActivity,
    lastActivityFull,
    // Store the raw timestamp data for potential further formatting
    lastActivityRaw,
  };
}

// Helper function to format timestamp to MM/DD/YYYY, hh:mm:ss AM/PM
function formatFullTimestamp(timestamp: any): string {
  if (!timestamp) return "No activity";

  let date: Date;

  // Handle Firestore timestamp object
  if (timestamp?.seconds) {
    date = new Date(timestamp.seconds * 1000);
  }
  // Handle Date object
  else if (timestamp instanceof Date) {
    date = timestamp;
  }
  // Handle numeric timestamp
  else if (typeof timestamp === "number") {
    date = new Date(timestamp * 1000);
  }
  // Handle string timestamp
  else if (typeof timestamp === "string") {
    date = new Date(timestamp);
  } else {
    return "Invalid timestamp";
  }

  if (isNaN(date.getTime())) return "Invalid date";

  return (
    date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    }) +
    ", " +
    date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    })
  );
}
// Add new async thunk to fetch organization data
export const fetchOrganization = createAsyncThunk(
  "profile/fetchOrganization",
  async (orgId: string, { rejectWithValue }) => {
    try {
      if (!orgId) return null;

      const orgDocRef = doc(db, `organizations/${orgId}`);
      const orgDoc = await getDoc(orgDocRef);

      if (orgDoc.exists()) {
        return {
          id: orgDoc.id,
          ...orgDoc.data(),
        };
      }

      return null;
    } catch (error) {
      console.error("Failed to fetch organization:", error);
      return rejectWithValue("Failed to fetch organization");
    }
  }
);

// Enhanced organization fetch with real-time updates
export const fetchOrganizations = createAsyncThunk(
  "profile/fetchOrganizations",
  async (orgIds: string[], { rejectWithValue, dispatch, getState }) => {
    try {
      const state = getState() as { profile: ProfileState };
      const cacheKey = "organizations";
      const now = Date.now();

      // Check if data exists in cache and is not expired
      const lastFetch = state.profile.lastFetchTimestamp[cacheKey] || 0;
      const isExpired = now - lastFetch > state.profile.cacheExpiry;

      // If data exists and cache is not expired, return cached data
      if (Object.keys(state.profile.organizations).length > 0 && !isExpired) {
        console.log("Using cached organizations data");
        return state.profile.organizations;
      }

      // Set up real-time listener for organizations
      const organizationsRef = collection(db, "organizations");

      const unsubscribe = onSnapshot(organizationsRef, (snapshot) => {
        if (!snapshot.metadata.fromCache) {
          console.log("Real-time update detected for organizations");

          const organizations: any = {};
          snapshot.docs.forEach((doc) => {
            const orgData = doc.data();
            organizations[doc.id] = {
              id: doc.id,
              name: orgData.name || "",
              billingAddress: orgData.billingAddress || {},
              url: orgData.url || "",
              taxId: orgData.taxId || "",
              teamName: orgData.teamName || "",
              ...orgData,
            };
          });

          dispatch({
            type: "profile/updateOrganizationsFromListener",
            payload: organizations,
            meta: { timestamp: Date.now() },
          });
        }
      });

      // Store the listener for cleanup
      dispatch({
        type: "profile/setActiveListener",
        payload: { key: cacheKey, listener: unsubscribe },
      });

      const organizations: any = {};

      // If specific orgIds provided, fetch only those
      if (orgIds && orgIds.length > 0) {
        for (const orgId of orgIds) {
          if (orgId) {
            const orgDocRef = doc(db, `organizations/${orgId}`);
            const orgDoc = await getDoc(orgDocRef);

            if (orgDoc.exists()) {
              const orgData = orgDoc.data();
              organizations[orgId] = {
                id: orgDoc.id,
                name: orgData.name || "",
                billingAddress: orgData.billingAddress || {},
                url: orgData.url || "",
                taxId: orgData.taxId || "",
                teamName: orgData.teamName || "",
                ...orgData,
              };
            }
          }
        }
      } else {
        // Fetch all organizations
        const orgSnapshot = await getDocs(organizationsRef);
        orgSnapshot.docs.forEach((doc) => {
          const orgData = doc.data();
          organizations[doc.id] = {
            id: doc.id,
            name: orgData.name || "",
            billingAddress: orgData.billingAddress || {},
            url: orgData.url || "",
            taxId: orgData.taxId || "",
            teamName: orgData.teamName || "",
            ...orgData,
          };
        });
      }

      return organizations;
    } catch (error) {
      console.error("Failed to fetch organizations:", error);
      return rejectWithValue("Failed to fetch organizations");
    }
  }
);

// Action to cleanup all listeners
export const cleanupAllListeners = () => (dispatch: any, getState: any) => {
  const state = getState();
  const activeListeners = state.profile.activeListeners;

  Object.keys(activeListeners).forEach((key) => {
    if (activeListeners[key]) {
      activeListeners[key](); // Call the unsubscribe function
      dispatch({ type: "profile/cleanupListener", payload: { key } });
    }
  });
};

// Cleanup profile listeners specifically
export const cleanupProfileListeners =
  (userId?: string) => (dispatch: any, getState: any) => {
    const state = getState();
    const activeListeners = state.profile.activeListeners;

    if (userId) {
      // Clean up specific user profile listeners
      const customerKey = `customerProfile_${userId}`;
      const expertKey = `expertProfile_${userId}`;

      if (activeListeners[customerKey]) {
        activeListeners[customerKey]();
        dispatch({
          type: "profile/cleanupListener",
          payload: { key: customerKey },
        });
      }

      if (activeListeners[expertKey]) {
        activeListeners[expertKey]();
        dispatch({
          type: "profile/cleanupListener",
          payload: { key: expertKey },
        });
      }
    } else {
      // Clean up all profile-related listeners
      Object.keys(activeListeners).forEach((key) => {
        if (key.includes("Profile_")) {
          activeListeners[key]();
          dispatch({ type: "profile/cleanupListener", payload: { key } });
        }
      });
    }
  };

// Real-time profile subscription setup
export const subscribeToUserProfile =
  (userId: string, userRole: string) => (dispatch: any) => {
    console.log(
      `Setting up real-time subscription for ${userRole} profile: ${userId}`
    );

    if (userRole === "Expert" || userRole === "expert_owner") {
      return dispatch(fetchExpertProfile(userId));
    } else if (userRole === "Customer" || userRole === "customer_owner") {
      return dispatch(fetchCustomerProfile(userId));
    }
  };

// Enhanced profile update with real-time sync
export const updateProfileWithRealTimeSync =
  (userId: string, profileData: any, userRole: string) => (dispatch: any) => {
    // Immediately update the store for instant UI feedback
    if (userRole === "Expert" || userRole === "expert_owner") {
      dispatch({
        type: "profile/updateExpertProfileFromListener",
        payload: { ...profileData, userId },
        meta: { cacheKey: `expertProfile_${userId}`, timestamp: Date.now() },
      });
    } else if (userRole === "Customer" || userRole === "customer_owner") {
      dispatch({
        type: "profile/updateCustomerProfileFromListener",
        payload: { ...profileData, userId },
        meta: { cacheKey: `customerProfile_${userId}`, timestamp: Date.now() },
      });
    }
  };

// Simple initialization function for directory component
export const initializeDirectorySync = () => (dispatch: any) => {
  console.log("Initializing directory real-time sync...");

  // Initialize real-time listeners for customers and experts
  dispatch(fetchAllUsersByRole("Customer" as "Customer" | "Expert"));
  dispatch(fetchAllUsersByRole("Expert" as "Customer" | "Expert"));

  // Initialize real-time listeners for session activities
  dispatch(fetchAllSessionActivities());

  // Initialize real-time listeners for chat sessions
  dispatch(fetchAllChatSessions());

  console.log("Directory real-time sync initialized successfully");
};

// Fast data update function for immediate UI feedback
export const updateDataImmediately =
  (type: string, data: any) => (dispatch: any) => {
    switch (type) {
      case "user":
        dispatch(immediateUserUpdate(data));
        break;
      case "organization":
        dispatch(immediateOrganizationUpdate(data));
        break;
      case "activity":
        dispatch(immediateActivityUpdate(data));
        break;
      default:
        console.warn("Unknown data type for immediate update:", type);
    }
  };

// Async function to fetch avg review and rating for a userId and role
export async function getAvgReviewAndRatingForUser(
  userId: string,
  role: string
) {
  // Determine field to match
  let fieldToMatch = "";
  if (role === "Expert") {
    fieldToMatch = "expertId";
  } else if (role === "Customer") {
    fieldToMatch = "customerId";
  } else {
    throw new Error("Invalid role specified");
  }

  // Query sessionActivity for this user
  const sessionActivitiesRef = collection(db, "sessionActivity");
  const activitiesQuery = query(
    sessionActivitiesRef,
    where(fieldToMatch, "==", userId)
  );
  const activitiesSnapshot = await getDocs(activitiesQuery);
  const activities = activitiesSnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));

  // Use the helper to calculate avgRating and avgReview
  return calculateAvgReviewAndRating(activities, userId, role);
}

// Async thunk to fetch user rating and reviews
export const fetchUserRatingAndReviews = createAsyncThunk(
  "profile/fetchUserRatingAndReviews",
  async (
    { userId, role }: { userId: string; role: string },
    { rejectWithValue }
  ) => {
    try {
      const result = await getAvgReviewAndRatingForUser(userId, role);
      return {
        userId,
        avgRating: result.avgRating,
        totalReviews: result.totalReviews,
        reviews: result.avgReview,
      };
    } catch (error: any) {
      return rejectWithValue(
        error?.message || "Failed to fetch user rating and reviews"
      );
    }
  }
);

// Add new async thunk to fetch quick access counts
export const fetchQuickAccessCounts = createAsyncThunk(
  "profile/fetchQuickAccessCounts",
  async (userId: string, { rejectWithValue, dispatch, getState }) => {
    try {
      if (!userId) throw new Error("No userId provided");

      const state = getState() as { profile: ProfileState };
      const cacheKey = `quickAccessCount_${userId}`;

      // Clean up existing listener if any
      const existingListener = state.profile.activeListeners[cacheKey];
      if (existingListener) {
        existingListener();
      }

      const quickAccessRef = doc(db, `quickAccessCount/${userId}`);
      const userRef = doc(db, `users/${userId}`);

      // Set up real-time listener for quick access counts
      const unsubscribeQuickAccess = onSnapshot(quickAccessRef, async (quickAccessDoc) => {
        let quickAccessData = quickAccessDoc.exists() ? quickAccessDoc.data() : {};
        
        // Fetch user data to get purchasedPackages length
        let tooltipPackageCount = 0;
        try {
          const userDoc = await getDoc(userRef);
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const purchasedPackages = userData.purchasedPackages || [];
            tooltipPackageCount = purchasedPackages.length;
          }
        } catch (error) {
          console.error("Error fetching user purchasedPackages:", error);
        }

        const counts = {
          helpCenter: quickAccessData.helpCenter || 0,
          supportChat: quickAccessData.supportChat || 0,
          videoTutorials: quickAccessData.videoTutorials || 0,
          tooltipPackage: tooltipPackageCount,
        };
        dispatch({
          type: "profile/updateQuickAccessCountsFromListener",
          payload: counts,
        });
      });

      // Set up real-time listener for user document to track purchasedPackages changes
      const unsubscribeUser = onSnapshot(userRef, async (userDoc) => {
        let tooltipPackageCount = 0;
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const purchasedPackages = userData.purchasedPackages || [];
          tooltipPackageCount = purchasedPackages.length;
        }

        // Get current quick access data
        const quickAccessDoc = await getDoc(quickAccessRef);
        let quickAccessData = quickAccessDoc.exists() ? quickAccessDoc.data() : {};

        const counts = {
          helpCenter: quickAccessData.helpCenter || 0,
          supportChat: quickAccessData.supportChat || 0,
          videoTutorials: quickAccessData.videoTutorials || 0,
          tooltipPackage: tooltipPackageCount,
        };
        dispatch({
          type: "profile/updateQuickAccessCountsFromListener",
          payload: counts,
        });
      });

      // Store both listeners for cleanup
      const combinedUnsubscribe = () => {
        unsubscribeQuickAccess();
        unsubscribeUser();
      };
      
      dispatch({
        type: "profile/setActiveListener",
        payload: { key: cacheKey, listener: combinedUnsubscribe },
      });

      // Return initial value for compatibility
      const quickAccessDoc = await getDoc(quickAccessRef);
      const userDoc = await getDoc(userRef);
      
      let tooltipPackageCount = 0;
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const purchasedPackages = userData.purchasedPackages || [];
        tooltipPackageCount = purchasedPackages.length;
      }

      if (!quickAccessDoc.exists()) {
        return {
          helpCenter: 0,
          supportChat: 0,
          videoTutorials: 0,
          tooltipPackage: tooltipPackageCount,
        };
      }
      const data = quickAccessDoc.data();
      return {
        helpCenter: data.helpCenter || 0,
        supportChat: data.supportChat || 0,
        videoTutorials: data.videoTutorials || 0,
        tooltipPackage: tooltipPackageCount,
      };
    } catch (error) {
      return rejectWithValue("Failed to fetch quick access counts");
    }
  }
);

// Add new async thunk to fetch all users' quick access counts with real-time updates
export const fetchAllUsersQuickAccessCounts = createAsyncThunk(
  "profile/fetchAllUsersQuickAccessCounts",
  async (_, { rejectWithValue, dispatch, getState }) => {
    try {
      const cacheKey = "allUsersQuickAccessCounts";
      const state = getState() as { profile: ProfileState };

      // Clean up existing listener if any
      const existingListener = state.profile.activeListeners[cacheKey];
      if (existingListener) {
        existingListener();
      }

      const quickAccessRef = collection(db, "quickAccessCount");
      const usersRef = collection(db, "users");

      // Set up real-time listener for quick access counts
      const unsubscribeQuickAccess = onSnapshot(quickAccessRef, async (quickAccessSnapshot) => {
        let totalHelpCenter = 0;
        let totalSupportChat = 0;
        let totalVideoTutorials = 0;

        quickAccessSnapshot.docs.forEach((doc) => {
          const data = doc.data();
          totalHelpCenter += data.helpCenter || 0;
          totalSupportChat += data.supportChat || 0;
          totalVideoTutorials += data.videoTutorials || 0;
        });

        // Fetch total tooltip packages from all users
        let totalTooltipPackage = 0;
        try {
          const usersSnapshot = await getDocs(usersRef);
          usersSnapshot.docs.forEach((userDoc) => {
            const userData = userDoc.data();
            const purchasedPackages = userData.purchasedPackages || [];
            totalTooltipPackage += purchasedPackages.length;
          });
        } catch (error) {
          console.error("Error fetching users purchasedPackages:", error);
        }

        dispatch({
          type: "profile/updateQuickAccessCountsFromListener",
          payload: {
            helpCenter: totalHelpCenter,
            supportChat: totalSupportChat,
            videoTutorials: totalVideoTutorials,
            tooltipPackage: totalTooltipPackage,
          },
        });
      });

      // Set up real-time listener for users collection to track purchasedPackages changes
      const unsubscribeUsers = onSnapshot(usersRef, async (usersSnapshot) => {
        let totalTooltipPackage = 0;
        usersSnapshot.docs.forEach((userDoc) => {
          const userData = userDoc.data();
          const purchasedPackages = userData.purchasedPackages || [];
          totalTooltipPackage += purchasedPackages.length;
        });

        // Get current quick access data
        const quickAccessSnapshot = await getDocs(quickAccessRef);
        let totalHelpCenter = 0;
        let totalSupportChat = 0;
        let totalVideoTutorials = 0;

        quickAccessSnapshot.docs.forEach((doc) => {
          const data = doc.data();
          totalHelpCenter += data.helpCenter || 0;
          totalSupportChat += data.supportChat || 0;
          totalVideoTutorials += data.videoTutorials || 0;
        });

        dispatch({
          type: "profile/updateQuickAccessCountsFromListener",
          payload: {
            helpCenter: totalHelpCenter,
            supportChat: totalSupportChat,
            videoTutorials: totalVideoTutorials,
            tooltipPackage: totalTooltipPackage,
          },
        });
      });

      // Store both listeners for cleanup
      const combinedUnsubscribe = () => {
        unsubscribeQuickAccess();
        unsubscribeUsers();
      };

      dispatch({
        type: "profile/setActiveListener",
        payload: { key: cacheKey, listener: combinedUnsubscribe },
      });

      // Initial fetch for compatibility
      const quickAccessSnapshot = await getDocs(quickAccessRef);
      const usersSnapshot = await getDocs(usersRef);

      let totalHelpCenter = 0;
      let totalSupportChat = 0;
      let totalVideoTutorials = 0;
      let totalTooltipPackage = 0;

      // Calculate totals from quick access counts
      quickAccessSnapshot.docs.forEach((doc) => {
        const data = doc.data();
        totalHelpCenter += data.helpCenter || 0;
        totalSupportChat += data.supportChat || 0;
        totalVideoTutorials += data.videoTutorials || 0;
      });

      // Calculate total tooltip packages from users
      usersSnapshot.docs.forEach((userDoc) => {
        const userData = userDoc.data();
        const purchasedPackages = userData.purchasedPackages || [];
        totalTooltipPackage += purchasedPackages.length;
      });

      return {
        helpCenter: totalHelpCenter,
        supportChat: totalSupportChat,
        videoTutorials: totalVideoTutorials,
        tooltipPackage: totalTooltipPackage,
      };
    } catch (error) {
      console.error("Failed to fetch all users quick access counts:", error);
      return rejectWithValue("Failed to fetch all users quick access counts");
    }
  }
);

// Add new async thunk to fetch chat sessions
export const fetchChatSessions = createAsyncThunk(
  "profile/fetchChatSessions",
  async (userId: string) => {
    try {
      const chatSessionsRef = collection(db, "chatSessions");
      const q = query(
        chatSessionsRef,
        where("userId", "==", userId)
        // orderBy("createdAt", "desc")
      );

      const querySnapshot = await getDocs(q);
      const chatSessions: ChatSession[] = [];

      querySnapshot.forEach((doc) => {
        chatSessions.push({
          id: doc.id,
          ...doc.data(),
        } as ChatSession);
      });

      return chatSessions;
    } catch (error) {
      console.error("Error fetching chat sessions:", error);
      throw error;
    }
  }
);

// Add new async thunk to fetch all chat sessions with real-time updates and caching
export const fetchAllChatSessions = createAsyncThunk(
  "profile/fetchAllChatSessions",
  async (_, { getState, rejectWithValue, dispatch }) => {
    try {
      const state = getState() as { profile: ProfileState };
      const cacheKey = "allChatSessions";
      const now = Date.now();

      // Check if data exists in cache and is not expired
      const lastFetch = state.profile.chatSessionsLastFetch || 0;
      const isExpired = now - lastFetch > state.profile.cacheExpiry;

      // If data exists and cache is not expired, return cached data
      if (state.profile.allChatSessions.length > 0 && !isExpired) {
        console.log("Using cached chat sessions data");
        return state.profile.allChatSessions;
      }

      // Clean up existing listener if any
      const existingListener = state.profile.activeListeners[cacheKey];
      if (existingListener) {
        existingListener();
      }

      return await withSentryTransaction(
        "fetchAllChatSessions",
        "data",
        async () => {
          // Reference to the chatSessions collection
          const chatSessionsRef = collection(db, "chatSessions");

          // Set up real-time listener for future updates
          const unsubscribe = onSnapshot(chatSessionsRef, async (snapshot) => {
            if (!snapshot.metadata.fromCache) {
              console.log("Real-time update detected for chat sessions");

              if (snapshot.empty) {
                dispatch({
                  type: "profile/updateChatSessionsFromListener",
                  payload: [],
                  meta: { timestamp: Date.now() },
                });
                return;
              }

              const chatSessions: ChatSession[] = [];
              snapshot.docs.forEach((doc) => {
                chatSessions.push({
                  id: doc.id,
                  ...doc.data(),
                } as ChatSession);
              });

              // Sort sessions by createdAt (newest first)
              chatSessions.sort((a, b) => {
                const dateA = a.createdAt?.toDate
                  ? a.createdAt.toDate()
                  : new Date(a.createdAt || 0);
                const dateB = b.createdAt?.toDate
                  ? b.createdAt.toDate()
                  : new Date(b.createdAt || 0);
                return dateB.getTime() - dateA.getTime();
              });

              // Update store with real-time data
              dispatch({
                type: "profile/updateChatSessionsFromListener",
                payload: chatSessions,
                meta: { timestamp: Date.now() },
              });
            }
          });

          // Store the listener for cleanup
          dispatch({
            type: "profile/setActiveListener",
            payload: { key: cacheKey, listener: unsubscribe },
          });

          // Initial fetch
          const chatSessionsSnapshot = await getDocs(chatSessionsRef);

          if (chatSessionsSnapshot.empty) {
            return [];
          }

          const chatSessions: ChatSession[] = [];
          chatSessionsSnapshot.docs.forEach((doc) => {
            chatSessions.push({
              id: doc.id,
              ...doc.data(),
            } as ChatSession);
          });

          // Sort sessions by createdAt (newest first)
          chatSessions.sort((a, b) => {
            const dateA = a.createdAt?.toDate
              ? a.createdAt.toDate()
              : new Date(a.createdAt || 0);
            const dateB = b.createdAt?.toDate
              ? b.createdAt.toDate()
              : new Date(b.createdAt || 0);
            return dateB.getTime() - dateA.getTime();
          });

          return chatSessions;
        }
      );
    } catch (error) {
      console.error("Failed to fetch all chat sessions:", error);
      return rejectWithValue("Failed to fetch all chat sessions");
    }
  }
);
